import { Button, Drawer, Flex } from '@mantine/core';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Select, TextInput } from 'react-hook-form-mantine';
import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getDealAdmins } from '@dealstack/frontend/app/services/Person.service.ts';
import {
	getProjectRepresentative,
	updateProjectRepresentative,
} from '@dealstack/frontend/shared/dataExchange/services/project.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	dealId: string | null;
}

export const ProjectCustomizationDrawer = ({ opened, onClose, dealId }: Props) => {
	const getUsersRequest = useQuery({
		queryKey: [API_PATHS.DEAL_DEALID_USERS_GET, { dealId: dealId as string }],
		queryFn: ({ signal }) =>
			getDealAdmins(API_PATHS.DEAL_DEALID_USERS_GET, { dealId: dealId as string }, signal),
		enabled: !!dealId,
	});

	const getProjectRepresentativeRequest = useQuery({
		queryKey: [API_PATHS.DEAL_DEALID_PROJECT_REPRESENTATIVE_GET, { dealId: dealId as string }],
		queryFn: () =>
			getProjectRepresentative(API_PATHS.DEAL_DEALID_PROJECT_REPRESENTATIVE_GET, {
				dealId: dealId as string,
			}),
		enabled: !!dealId,
	});

	useEffect(() => {
		if (!getProjectRepresentativeRequest.isSuccess) {
			return;
		}
		const data = getProjectRepresentativeRequest.data;
		form.reset({
			companyRepresentative: data?.user?.id ?? '',
			companyRepresentativePosition: data?.jobTitle ?? '',
		});
	}, [getProjectRepresentativeRequest.isSuccess, getProjectRepresentativeRequest.data]);

	const form = useForm({
		mode: 'all',
		defaultValues: { companyRepresentative: '', companyRepresentativePosition: '' },
		resolver: zodResolver(
			z.object({
				companyRepresentative: z.string().nonempty(),
				companyRepresentativePosition: z.string().nonempty(),
			})
		),
	});

	const onModalClose = () => {
		form.reset({ companyRepresentative: '', companyRepresentativePosition: '' });
		onClose();
	};

	const updateProjectRepresentativeRequest = useMutation({
		mutationFn: updateProjectRepresentative,
	});

	const onSubmit = () => {
		const { companyRepresentative, companyRepresentativePosition } = form.getValues();

		updateProjectRepresentativeRequest.mutate(
			{
				dealId: dealId as string,
				body: {
					userId: companyRepresentative,
					jobTitle: companyRepresentativePosition,
				},
			},
			{
				onSuccess: () => {
					showToastV2();
					onModalClose();
				},
			}
		);
	};

	const usersOptions = useMemo(() => {
		return (
			getUsersRequest.data?.map((user) => ({
				label: `${user.firstName} ${user.lastName} (${user.email})`,
				value: user.id,
			})) ?? []
		);
	}, [getUsersRequest.data]);

	const getNothingFoundText = () => {
		if (!usersOptions.length) {
			return 'Start typing to search for a user';
		}
		if (getUsersRequest.isFetching) {
			return 'Loading...';
		}
		if (getUsersRequest.data?.length) {
			return 'Not found';
		}
	};

	const isFetching = getUsersRequest.isFetching || getProjectRepresentativeRequest.isFetching;

	return (
		<Drawer
			opened={opened}
			onClose={onModalClose}
			title="Project details"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			position="right"
		>
			{isFetching && <InlineLoader />}
			<Flex direction="column" gap="2rem" h="100%">
				<Select
					control={form.control}
					data={usersOptions}
					name="companyRepresentative"
					label="Company representative"
					placeholder="Start typing to search for a user"
					searchable
					size="md"
					nothingFoundMessage={getNothingFoundText()}
					disabled={updateProjectRepresentativeRequest.isPending}
					allowDeselect
					clearable
				/>

				<TextInput
					control={form.control}
					name="companyRepresentativePosition"
					label="Company representative position"
					placeholder="Company representative position"
					size="md"
					disabled={updateProjectRepresentativeRequest.isPending}
				/>

				<Button
					mt="auto"
					onClick={form.handleSubmit(onSubmit)}
					disabled={updateProjectRepresentativeRequest.isPending}
				>
					Confirm
				</Button>
			</Flex>
		</Drawer>
	);
};
